'use client';

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '~/lib/utils';

export type EmptyStateProps = {
  title: string;
  description: string;
  /** Lucide icon rendered in a muted tile when `illustration` is omitted. */
  icon?: LucideIcon;
  /** Custom mark (SVG, etc.). Wins over `icon`. */
  illustration?: ReactNode;
  /** Emphasized primary action already present on this screen (Invite, Add, …). */
  action?: ReactNode;
  /** `page` fills the column; `section` is a large panel empty; `panel` is a compact centered tray; `compact` is an in-list row. */
  size?: 'page' | 'section' | 'panel' | 'compact';
  className?: string;
};

/**
 * Shared empty emphasis: illustration → title → muted description → optional CTA.
 */
export function EmptyState({
  title,
  description,
  icon: Icon,
  illustration,
  action,
  size = 'page',
  className,
}: EmptyStateProps) {
  const isCompact = size === 'compact';
  const isPanel = size === 'panel';

  return (
    <div
      className={cn(
        isCompact
          ? 'flex flex-col items-start gap-3 px-4 py-3.5 text-left sm:flex-row sm:items-center'
          : 'flex flex-col items-center justify-center text-center',
        isPanel ? 'space-y-1.5 px-4 py-6' : !isCompact && 'px-6',
        size === 'page' && 'min-h-[min(28rem,calc(100vh-14rem))]',
        size === 'section' && 'min-h-48 py-10',
        className,
      )}
      role="status"
    >
      {illustration ? (
        <div className={cn(isCompact ? 'shrink-0' : isPanel ? 'mb-0' : 'mb-6')}>{illustration}</div>
      ) : Icon ? (
        <div
          className={cn(
            'bg-muted flex items-center justify-center ring-1 ring-border/60',
            isCompact && 'size-9 shrink-0 rounded-[10px]',
            isPanel && 'size-10 rounded-[10px]',
            !isCompact && !isPanel && 'mb-6 size-[7.5rem] rounded-2xl',
          )}
        >
          <Icon
            className={cn(
              'text-muted-foreground',
              isCompact && 'size-4',
              isPanel && 'size-5',
              !isCompact && !isPanel && 'size-10',
            )}
            strokeWidth={isCompact || isPanel ? 2 : 1.5}
            aria-hidden
          />
        </div>
      ) : null}

      <div className={cn(isCompact && 'min-w-0 flex-1')}>
        <h2
          className={cn(
            'text-foreground font-semibold tracking-tight',
            (isCompact || isPanel) && 'text-sm',
            !isCompact && !isPanel && 'text-base',
          )}
        >
          {title}
        </h2>
        <p
          className={cn(
            'text-muted-foreground',
            isCompact && 'mt-0.5 text-xs leading-4',
            isPanel && 'max-w-sm text-sm leading-5',
            !isCompact && !isPanel && 'mt-2 max-w-sm text-sm leading-5',
          )}
        >
          {description}
        </p>
      </div>

      {action ? (
        <div
          className={cn(
            isCompact ? 'shrink-0' : isPanel ? 'mt-2 flex items-center justify-center' : 'mt-6 flex items-center justify-center',
          )}
        >
          {action}
        </div>
      ) : null}
    </div>
  );
}
